export const MAX_REF_LENGTH = 256;
export const NAMESPACES = ['tenant', 'location', 'display', 'campaign', 'var', 'data', 'time', 'date', 'player', 'event'];
/** Namespaces evaluated only in the Cloud (live player state, Flow trigger payloads). */
export const CLOUD_NAMESPACES = ['player', 'event'];
/** Engine-owned references (computed from `now` + display zone) and their types. */
export const ENGINE_REFS = {
    'time.local': 'time',
    'date.local': 'date',
    'date.weekday': 'number',
    'date.day': 'number',
    'date.month': 'number',
    'date.year': 'number',
};
// Keyed selection `[?field=value]` matches DataPath / renderer template.ts (DATA_SOURCES.md §6).
const REF_RE = /^[a-z][a-z0-9_]{0,31}(\.[A-Za-z0-9_]+|\[[0-9]+\]|\[\?[A-Za-z0-9_]+=[A-Za-z0-9_.:@-]{1,64}\])*$/;
const FIRST_KEY_RE = /^[a-z][a-z0-9_]{0,31}\.([A-Za-z0-9_]+)/;
const VAR_KEY_RE = /^[a-z][a-z0-9_]{0,47}$/;
const SOURCE_KEY_RE = /^[a-z][a-z0-9_]{0,31}$/;
export function namespaceOf(ref) {
    const m = /^[a-z][a-z0-9_]*/.exec(ref);
    return m ? m[0] : '';
}
export function isEngineRef(ref) {
    return Object.prototype.hasOwnProperty.call(ENGINE_REFS, ref);
}
/** Checks a reference's grammar and namespace rules. */
export function checkRef(ref) {
    if (ref.length === 0 || ref.length > MAX_REF_LENGTH || !REF_RE.test(ref)) {
        return { ok: false, code: 'invalid_ref', message: `"${ref.slice(0, 64)}" is not a valid reference (namespace.key with .key / [index] segments).` };
    }
    const ns = namespaceOf(ref);
    if (NAMESPACES.indexOf(ns) < 0) {
        return { ok: false, code: 'unknown_namespace', message: `Unknown namespace "${ns}".` };
    }
    const first = FIRST_KEY_RE.exec(ref);
    if (!first) {
        return { ok: false, code: 'invalid_ref', message: `"${ref}" needs a key after the namespace (e.g. ${ns}.name).` };
    }
    const key = first[1];
    if (ns === 'time' || ns === 'date') {
        if (!isEngineRef(ref))
            return { ok: false, code: 'unknown_ref', message: `Unknown reference "${ref}" (time.local, date.local, date.weekday, date.day, date.month, date.year).` };
    }
    else if (ns === 'var' && !VAR_KEY_RE.test(key)) {
        return { ok: false, code: 'invalid_ref', message: `"${key}" is not a valid variable key.` };
    }
    else if (ns === 'data' && !SOURCE_KEY_RE.test(key)) {
        return { ok: false, code: 'invalid_ref', message: `"${key}" is not a valid data source key.` };
    }
    return { ok: true, namespace: ns };
}
/** Data source key of a `data.<key>…` reference, or null. */
export function dataSourceKey(ref) {
    if (namespaceOf(ref) !== 'data')
        return null;
    const m = FIRST_KEY_RE.exec(ref);
    return m ? m[1] : null;
}
/**
 * Looks a reference up in a map whose keys are exact refs or `prefix.*` wildcards (matching
 * `prefix.…` and `prefix[…`). Exact keys win; otherwise the longest matching prefix.
 */
export function lookupRef(map, ref) {
    if (Object.prototype.hasOwnProperty.call(map, ref))
        return { found: true, value: map[ref] };
    let best = -1;
    let value;
    for (const k of Object.keys(map)) {
        if (k.length < 3 || k.slice(-2) !== '.*')
            continue;
        const prefix = k.slice(0, -2);
        if (ref.length > prefix.length && ref.slice(0, prefix.length) === prefix) {
            const next = ref.charAt(prefix.length);
            if ((next === '.' || next === '[') && prefix.length > best) {
                best = prefix.length;
                value = map[k];
            }
        }
    }
    return { found: best >= 0, value };
}
//# sourceMappingURL=refs.js.map